// What the CONVERSATION says is still owed, when the order says nothing is.
//
// ─── WHY THIS SCREEN EXISTS ─────────────────────────────────────────────────
//
// Owner, 2026-08-24, after the data-driven resume sweep shipped:
//
//   "there are some orders, it looks like it's done, but you need to actually
//    add, like, another album or a continuing, some edits… All of that you will
//    know by the context of the WhatsApp conversation."
//
// Every other queue in this admin reads the ORDER — this album has no photos,
// that one has no cover. Each of those gaps is a hole in a database column.
// These are different: they exist ONLY in the messages.
//
//   « ghadi nsift lik tsawer dyal l'album 2 ghedda »   — never arrived
//   « bdel tswira dyal page 4 »                        — nobody did it
//   « bghit nzid wahed album »                         — agreed to, never added
//
// ─── IT PROPOSES. NOTHING HERE HAS MESSAGED ANYBODY. ────────────────────────
//
// ⚠️ SAME RULE AS THE RECEIPTS CARD, FOR THE SAME REASON. « The customer asked
// for edits » is a judgement about what somebody meant, not a value read off a
// slip — and every one of these needs a person anyway: adding an album reprices
// the order, making an edit is work somebody has to do. So the assistant finds
// them and stops.
//
// ⚠️ AND THE QUOTE IS THE CUSTOMER'S, VERBATIM. The worker refuses any quote
// that is not in the transcript character for character, so what is on this
// card is always something that was really said. That is what makes the list
// worth trusting enough to act on.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ordersApi, settingsApi, type OpenLoop, type OpenLoopKind } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { SettingGroup } from './_ui';

/** What each kind is, in Yahya's words. Mirrors `OPEN_LOOP_LABEL` in the
 *  worker — the worker's copy is what goes in the order note, this one is what
 *  goes on the screen. */
export const LOOP_LABEL: Record<OpenLoopKind, string> = {
  add_album: 'Il a demandé un album en plus',
  edits: 'Il a demandé des modifications',
  photos_promised: 'Il a promis des photos qui ne sont jamais arrivées',
  promise_unkept: 'On lui a promis quelque chose qu’on n’a pas fait',
};

/** And what to do about it. */
export const LOOP_NEXT: Record<OpenLoopKind, string> = {
  add_album: 'Ajouter l’album et refaire le prix, ou lui dire que ce n’est plus possible.',
  edits: 'Faire la modification demandée, puis relancer l’aperçu.',
  photos_promised: 'Le relancer pour les photos manquantes.',
  promise_unkept: 'Tenir la promesse, ou lui expliquer pourquoi elle a changé.',
};

/**
 * How long this has been waiting, in plain words.
 *
 * ⚠️ AGE IS THE WHOLE PRIORITY SIGNAL ON THIS CARD, so it is a function rather
 * than JSX. A request from three weeks ago and one from this morning need
 * completely different handling, and « 2026-08-03 » does not say that at a
 * glance — the reader has to do the arithmetic, and stops doing it by the
 * fourth row.
 *
 * Exported because it is the only real judgement here, and a judgement should
 * not hide inside a template.
 */
export function waitedFor(askedAt: string | null | undefined, nowMs = Date.now()): string {
  const t = Date.parse(String(askedAt ?? ''));
  if (!Number.isFinite(t)) return '';
  const days = Math.floor((nowMs - t) / 86_400_000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} jours`;
  const months = Math.round(days / 30);
  return months <= 1 ? 'il y a environ un mois' : `il y a environ ${months} mois`;
}

/**
 * Is this old enough to look urgent?
 *
 * ⚠️ NOT COLOUR ALONE — the row also SAYS how long it has waited. The tint only
 * reinforces what the words already carry, so a colour-blind reader loses
 * nothing.
 */
export function isStale(askedAt: string | null | undefined, nowMs = Date.now()): boolean {
  const t = Date.parse(String(askedAt ?? ''));
  return Number.isFinite(t) && nowMs - t > 7 * 86_400_000;
}

function Row({ l }: { l: OpenLoop }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (decision: 'done' | 'dismiss') => ordersApi.decideOpenLoop(l.id, { decision }),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ['open-loops'] }); },
    onError: (e: Error) => setError(e.message),
  });

  const waited = waitedFor(l.asked_at);
  const stale = isStale(l.asked_at);
  const orderName = l.order?.shopify_order_name ?? t('Commande inconnue');

  return (
    <div className={`rounded-xl border px-3.5 py-3 ${
      stale ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200 bg-white'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-gray-900 truncate">
            {l.order?.shopify_order_id ? (
              <a
                href={`/admin/orders/${l.order.shopify_order_id}`}
                className="underline underline-offset-2 decoration-gray-300"
              >
                {orderName}
              </a>
            ) : orderName}
          </p>
          <p className="text-[11.5px] text-gray-500 truncate">
            {l.order?.customer_name ?? t('Sans nom')}
            {l.order?.paid_at ? ` · ${t('déjà payée')}` : ''}
          </p>
        </div>
        {/* The age carries its meaning in words; the colour only repeats it. */}
        {waited && (
          <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            stale ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-600'
          }`}>
            {t(waited)}
          </span>
        )}
      </div>

      <p className="mt-2 text-[13px] font-semibold text-gray-900">
        {t(LOOP_LABEL[l.kind] ?? l.kind)}
        {l.album_index ? ` — ${t('album')} ${l.album_index}` : ''}
      </p>

      {/* ⚠️ THE CUSTOMER'S OWN WORDS, AND ONLY EVER THOSE. The worker refuses a
          quote that is not in the transcript, so nothing here is a summary of
          what somebody "basically said". */}
      {l.quote && (
        <blockquote className="mt-1.5 text-[12.5px] text-gray-700 border-l-2 border-gray-300 pl-2.5">
          « {l.quote} »
        </blockquote>
      )}

      <p className="mt-2 text-[11.5px] text-gray-500">{t(LOOP_NEXT[l.kind] ?? '')}</p>

      {error && <p className="mt-2 text-[11.5px] text-red-600" role="alert">{error}</p>}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => decide.mutate('done')}
          disabled={decide.isPending}
          className="h-11 px-4 rounded-lg text-[13px] font-semibold bg-emerald-600 text-white
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {decide.isPending ? t('Enregistrement…') : t('C’est fait')}
        </button>
        <button
          type="button"
          onClick={() => decide.mutate('dismiss')}
          disabled={decide.isPending}
          className="h-11 px-4 rounded-lg text-[13px] font-semibold border border-gray-300
                     text-gray-700 disabled:opacity-40"
        >
          {t('Pas concerné')}
        </button>
      </div>
    </div>
  );
}

/**
 * The on-switch, on the card it feeds.
 *
 * ⚠️ OFF UNTIL HE TURNS IT ON. The owner's standing rule for a new integration,
 * and this one earns it twice: the sweep reads real customer conversations
 * through a model, and the day it starts it works through every conversation
 * nobody has read yet. That is a decision, not a deploy side effect.
 *
 * ⚠️ AND IT LIVES HERE RATHER THAN IN THE SETTINGS PAGE. A switch parked three
 * screens away from the list it controls is a switch nobody finds — the empty
 * list would read as "nothing to do" when it actually means "nothing is
 * looking".
 */
function Switch() {
  const { t } = useT();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });
  const on = data?.settings?.open_loops_enabled === true;

  const flip = useMutation({
    mutationFn: (next: boolean) => settingsApi.update({ open_loops_enabled: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  return (
    <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox"
        checked={on}
        disabled={flip.isPending}
        onChange={(e) => flip.mutate(e.target.checked)}
        className="mt-0.5 h-5 w-5 rounded border-gray-300 accent-amber-600"
      />
      <span className="text-[12px] text-gray-700">
        <span className="font-semibold">{t('Relire les conversations')}</span>
        <span className="block text-[11.5px] text-gray-500">
          {t(
            'Quand c’est coché, l’assistant relit une conversation par commande et par jour. '
            + 'Il n’envoie jamais de message : il remplit seulement cette liste.',
          )}
        </span>
      </span>
    </label>
  );
}

export function OpenLoopsCard() {
  const { t } = useT();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['open-loops'],
    queryFn: () => ordersApi.openLoops(),
    refetchInterval: 120_000,
  });

  const loops = data?.loops ?? [];
  const { data: cfg } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });
  const enabled = cfg?.settings?.open_loops_enabled === true;

  return (
    <SettingGroup
      title={t('Promesses en attente')}
      summary={
        isLoading
          ? t('Chargement…')
          : loops.length === 0
            ? t('Rien en attente')
            : `${loops.length} ${t('chose(s) promise(s) et jamais faite(s)')}`
      }
      // Opens itself when something is waiting — a promise behind a closed
      // accordion is the pile-up this exists to prevent.
      tone={loops.length > 0 ? 'sensitive' : 'default'}
      defaultOpen={loops.length > 0}
      loading={isLoading}
    >
      <Switch />

      <p className="text-[11.5px] text-gray-500 mt-3">
        {t(
          'L’assistant relit les conversations WhatsApp et repère ce qui a été promis ou '
          + 'demandé sans jamais être fait — un album en plus, une modification, des photos '
          + 'annoncées. Il n’envoie rien : c’est à vous de traiter, puis de fermer la ligne.',
        )}
      </p>

      {isError && (
        <p className="text-[11.5px] text-red-600 mt-3" role="alert">
          {t('Impossible de charger les promesses en attente.')}
        </p>
      )}

      {/* ⚠️ « NOTHING TO DO » AND « NOTHING IS LOOKING » MUST NOT LOOK THE SAME.
          An empty list with the switch off is the more common of the two and
          the more misleading — it reads as an all-clear. */}
      {!isLoading && !isError && loops.length === 0 && (
        <p className="text-[12px] text-gray-400 mt-3">
          {data?.installed === false
            ? t('Pas encore installé sur la base de données.')
            : enabled
              ? t('Aucune promesse en attente dans les conversations.')
              : t('Personne ne relit les conversations — cochez la case ci-dessus.')}
        </p>
      )}

      <div className="mt-3 space-y-2">
        {loops.map((l) => <Row key={l.id} l={l} />)}
      </div>
    </SettingGroup>
  );
}
